import { type ReactNode, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useModalPanel, usePanelMotion } from '../lib/use-modal-panel';
import s from './Sheet.module.css';

/**
 * A modal side sheet from the right (design v3 "Chi tiết" on the dashboard): a title, a close button and a
 * scrolling body, as wide as a member's drawer so one can open over the other without a jump. It slides in
 * and out (✕, scrim, Esc). While a drawer covers it (`covered`), it steps 56px to the left under that
 * drawer's scrim; the drawer takes the keys until it closes.
 */
export function Sheet({
  title,
  onClose,
  covered = false,
  children,
}: {
  title: string;
  /** Called once the sheet has slid out. */
  onClose: () => void;
  covered?: boolean;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const panel = useRef<HTMLElement>(null);
  const titleId = useId();
  const { shown, close } = usePanelMotion(onClose);
  useModalPanel(panel, close);
  return createPortal(
    <>
      <div className={s.scrim} data-shown={shown} onClick={close} aria-hidden="true" />
      <aside
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={s.sheet}
        data-shown={shown}
        data-covered={covered}
      >
        <div className={s.bar}>
          <h2 id={titleId} className={s.title}>
            {title}
          </h2>
          <button
            type="button"
            className={s.close}
            aria-label={t('common.close')}
            title={t('members.closeEsc')}
            onClick={close}
          >
            ✕
          </button>
        </div>
        <div className={s.body}>{children}</div>
      </aside>
    </>,
    document.body,
  );
}
